import {FC, Fragment} from 'react';
import {Collapse, Divider, Stack, styled, Table, TableBody, TableCell, TableRow, Typography} from '@mui/material';
import {blue, grey} from '@mui/material/colors';
import {DocumentDto, ImportDto} from '../../../../global/api';
import {colors} from '../../../../shared/colors';
import {ContractDataImportsDownloadButton} from './contract-data-imports-download-button';
import {get} from 'lodash';

interface Props {
  data: ImportDto;
  expanded: boolean;
}

export const ContractDataImportsTableCollapseRow: FC<Props> = ({data, expanded}) => {
  const {
    errorDocument,
    totalRowCount = 0,
    updateRowCount = 0,
    addRowCount = 0,
    skipRowCount = 0,
    failedRowCount = 0,
  } = data;

  const hasDescription = Boolean(errorDocument?.description);

  return (
    <StyledTableRow bgColor={colors['bg--on-white']}>
      <StyledTableCell noPaddingY colSpan={8}>
        <Collapse in={expanded} timeout="auto">
          <Stack m={1}>
            <ImportStatsTable
              total={totalRowCount}
              updated={updateRowCount}
              added={addRowCount}
              skipped={skipRowCount}
              failed={failedRowCount}
              errorDocument={errorDocument}
            />
            {hasDescription && <CommentSection text={errorDocument?.description || ''} />}
          </Stack>
        </Collapse>
      </StyledTableCell>
    </StyledTableRow>
  );
};

const ImportStatsTable: FC<{
  total: number;
  updated: number;
  added: number;
  skipped: number;
  failed: number;
  errorDocument?: DocumentDto;
}> = ({total, updated, added, skipped, failed, errorDocument}) => (
  <Table size="small">
    <TableBody>
      <TableRow>
        <StyledTableCell noPadding>Записей всего</StyledTableCell>
        <StyledTableCell noPadding>
          <StyledTypography textColor="green">{total}</StyledTypography>
        </StyledTableCell>

        <StyledTableCell noPadding>
          <Stack direction="column" spacing={0.5}>
            <StyledTypography>Обновлено</StyledTypography>
            <StyledTypography>Добавлено</StyledTypography>
            <StyledTypography>Пропущено</StyledTypography>
          </Stack>
        </StyledTableCell>

        <StyledTableCell noPadding>
          <Stack px={1} direction="column" spacing={0.5}>
            <StyledTypography textColor="blue">{updated}</StyledTypography>
            <StyledTypography textColor="green">{added}</StyledTypography>
            <StyledTypography textColor="grey">{skipped}</StyledTypography>
          </Stack>
        </StyledTableCell>

        <StyledTableCell noPadding>С ошибками</StyledTableCell>
        <StyledTableCell noPadding>
          <StyledTypography textColor="red">{failed}</StyledTypography>
        </StyledTableCell>

        <StyledTableCell noPadding align="left">
          <ContractDataImportsDownloadButton
            fileName={get(errorDocument, 'fileName')}
            documentId={get(errorDocument, 'id')}
          />
        </StyledTableCell>
      </TableRow>
    </TableBody>
  </Table>
);

const CommentSection: FC<{text: string}> = ({text}) => (
  <Fragment>
    <Divider sx={{my: 1}} />
    <Stack direction="row" alignItems="center" m={1}>
      <StyledTypography mr={2} fontWeight={500}>
        Комментарий:
      </StyledTypography>
      <StyledTypography fontWeight={400}>{text}</StyledTypography>
    </Stack>
  </Fragment>
);

const COLORS = {
  green: colors.green,
  blue: blue[700],
  grey: grey[700],
  red: colors.red,
} as const;

const StyledTableRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== 'bgColor',
})<{bgColor?: string}>(({bgColor}) => ({
  backgroundColor: bgColor || 'transparent',
}));

const StyledTableCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'noPadding' && prop !== 'noPaddingY',
})<{noPadding?: boolean; noPaddingY?: boolean}>(({noPadding, noPaddingY}) => ({
  ...(noPadding && {padding: 0}),
  ...(noPaddingY && {paddingBottom: 0, paddingTop: 0}),
  maxWidth: '10rem',
  borderBottom: 0,
}));

const StyledTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{textColor?: keyof typeof COLORS}>(({textColor}) => ({
  fontSize: 14,
  ...(textColor && {color: COLORS[textColor]}),
}));
